/* eslint-disable react/prop-types */
import { ButtonPrimary, ButtonSecondary } from 'components/Button'
import Row from 'components/Row'
import useStepWithNext from 'components/StepLine/context/hooks/useStepWithNext'
import StepLineModal from 'components/StepLine/StepLineModal'
import { FarmError } from 'lib/hooks/kodiakFarms/useFarmAction'
import UpdateFarmConfigStep from 'pages/KodiakFarmsConfigure/UpdateFarmConfigStep'
import { useCallback, useMemo, useState } from 'react'
import { ArrowLeft, Info } from 'react-feather'
import { Text } from 'rebass'
import { ThemedText } from 'theme'

import { ReviewItem } from './RewardItem'
import { CardContent, CardHeader, Container, InfoAlert, InfoText, LoaderIcon, ReviewGrid } from './styled'
import { FlexBetween } from './styled'
import { FarmConfig } from './types'
import { hasConfigChanged } from './utils'
import { ReviewFormProps } from './utils/types'

// Helper function to get field descriptions
const fieldInfoMap: Record<string, string> = {
  'Rewards Duration':
    'Duration for each reward period cycle. Determines how long rewards will be distributed before needing renewal',
  'Staking Token Cap': 'Maximum amount of staking tokens allowed in the farm. Enter -1 for unlimited cap (max uint256)',
  'Lock Time for Max Multiplier':
    'Duration in seconds users must lock their tokens to receive the maximum reward multiplier. Must be greater than minimum lock time',
  'Minimum Lock Time':
    'Minimum duration in seconds users must lock their tokens. Users cannot withdraw before this period expires',
  'Maximum Multiplier':
    'Maximum reward multiplier that users can earn by locking tokens. E.g., 3 means users can earn up to 3x rewards',
}

const getFieldInfo = (field: string): string => {
  return fieldInfoMap[field] || ''
}

// Get changed general fields
const getChangedFields = (config: FarmConfig, originalConfig: FarmConfig) => {
  const fields: (keyof Omit<FarmConfig, 'rewardTokens'>)[] = [
    'rewardsDuration',
    'stakingTokenCap',
    'lockTimeMax',
    'lockTimeMin',
    'lockMultiplierMax',
  ]

  return fields.reduce<Array<{ field: string; current: string; previous: string }>>((acc, field) => {
    const currentValue = config[field]?.toString().trim()
    const originalValue = originalConfig[field]?.toString().trim()

    if (currentValue !== originalValue && currentValue !== '') {
      acc.push({
        field: field
          .replace(/([A-Z])/g, ' $1')
          .trim()
          .replace(/^./, (str) => str.toUpperCase()),
        current: currentValue,
        previous: originalValue,
      })
    }
    return acc
  }, [])
}

// Get changed reward tokens
const getChangedRewardTokens = (config: FarmConfig, originalConfig: FarmConfig) => {
  if (!config || !originalConfig) return []

  return config.rewardTokens
    .map((token, index) => {
      const originalToken = originalConfig.rewardTokens[index]

      if (!originalToken) {
        return {
          index,
          changes: [
            {
              field: 'New Reward Token',
              current: token.tokenAddress,
              previous: 'None',
            },
            {
              field: 'Reward Rate',
              current: token.rewardRate,
              previous: 'None',
            },
            {
              field: 'Reward Manager',
              current: token.rewardManager,
              previous: 'None',
            },
          ],
        }
      }

      // Handle changes to existing tokens
      const changes: { field: string; current: string; previous: string }[] = []

      if (token.tokenAddress !== originalToken.tokenAddress) {
        changes.push({
          field: 'Token Address',
          current: token.tokenAddress,
          previous: originalToken.tokenAddress,
        })
      }

      if (token.rewardRate !== originalToken.rewardRate) {
        changes.push({
          field: 'Reward Rate',
          current: token.rewardRate,
          previous: originalToken.rewardRate,
        })
      }

      if (token.rewardManager !== originalToken.rewardManager) {
        changes.push({
          field: 'Reward Manager',
          current: token.rewardManager,
          previous: originalToken.rewardManager,
        })
      }

      return changes.length > 0 ? { index, changes } : null
    })
    .filter(Boolean) as Array<{ index: number; changes: Array<{ field: string; current: string; previous: string }> }>
}

const ReviewForm: React.FC<ReviewFormProps> = ({
  config,
  originalConfig,
  onBack,
  loading,
  errors,
  approveSteps,
  updateConfig,
  refetch,
  checkRewardTokenBalances,
}) => {
  const [showStartModal, setShowStartModal] = useState<boolean>(false)
  const [updatingConfig, setUpdatingConfig] = useState<boolean>(false)
  const [updateTxHash, setUpdateTxHash] = useState<string>('')
  const [error, setError] = useState<FarmError | undefined>()

  // Memoize expensive calculations
  const rewardTokenHasChanged = useMemo(() => hasConfigChanged(config, originalConfig), [config, originalConfig])

  const changedRewardTokens = useMemo(() => getChangedRewardTokens(config, originalConfig), [config, originalConfig])

  const changedFields = useMemo(() => getChangedFields(config, originalConfig), [config, originalConfig])

  // Memoize callback functions
  const onUpdateConfig = useCallback(async () => {
    try {
      setUpdatingConfig(true)
      // We can directly call these since approvals are handled by steps
      const txn = await updateConfig(config, originalConfig)
      setUpdateTxHash(txn.hash || txn.transactionHash)
    } catch (err) {
      console.error(err)
      // throw err
    } finally {
      setUpdatingConfig(false)
    }
  }, [config, originalConfig, updateConfig])

  const updateConfigStep = useStepWithNext('updateConfig', UpdateFarmConfigStep, {
    config,
    originalConfig,
    changedFields,
    changedRewardTokens,
    onStart: onUpdateConfig,
    attemptingTxn: updatingConfig,
    pendingText: 'Updating farm configuration...',
    hash: updateTxHash,
    autoConfirm: false,
    waitForConfirmation: true,
  })

  // Steps for updating configuration
  const updateSteps = useMemo(() => {
    const steps = []

    // Only check for approvals if farm is live
    if (config?.rewardTokens && config.isLive) {
      const { approveStep0, approveStep1, approveStep2 } = approveSteps
      steps.push(approveStep0, approveStep1, approveStep2)
    }

    steps.push(updateConfigStep)
    return steps
  }, [approveSteps, config?.rewardTokens, config.isLive, updateConfigStep])

  const handleConfirm = useCallback(async () => {
    try {
      // Check if there are any new reward tokens
      const hasNewRewardTokens = config.rewardTokens.some(
        (token) =>
          !originalConfig.rewardTokens.some(
            (originalToken) => originalToken.tokenAddress.toLowerCase() === token.tokenAddress.toLowerCase()
          )
      )

      // Only check balances if farm is live and we have new tokens
      if (config.isLive && hasNewRewardTokens) {
        await checkRewardTokenBalances(config)
      }

      setShowStartModal(true)
    } catch (err) {
      console.error('Error showing update modal:', err)
      setError(err as FarmError)
    }
  }, [config, originalConfig, checkRewardTokenBalances])

  const handleModalDismiss = useCallback(() => {
    setShowStartModal(false)
  }, [])

  const handleModalFinish = useCallback(() => {
    setShowStartModal(false)
    refetch()
  }, [refetch])

  const handleBuyClick = useCallback((tokenAddress: string) => {
    window.open(`/swap?outputCurrency=${tokenAddress}`, '_blank')
  }, [])

  // If no changes, show info message
  if (!rewardTokenHasChanged && changedFields.length === 0) {
    return (
      <Container>
        <InfoAlert>
          <Info size={20} />
          <InfoText>No changes have been made to the farm configuration.</InfoText>
        </InfoAlert>
        <ButtonSecondary onClick={onBack} style={{ width: 'fit-content', gap: '0.5rem', marginTop: '1rem' }}>
          <ArrowLeft size={16} />
          Back to Edit
        </ButtonSecondary>
      </Container>
    )
  }

  // Add error rendering
  const renderError = (error: FarmError) => {
    if (!error) return null

    if (error.type === 'BALANCE_ERROR') {
      return (
        <Row width="100%">
          <InfoAlert>
            <Info size={20} />
            <InfoText>
              Insufficient {error.symbol} balance
              <br />
              Required: {error.required} {error.symbol}
              <br />
              Available: {error.available} {error.symbol}
            </InfoText>
          </InfoAlert>
          <Row style={{ maxWidth: '120px' }}>
            <ButtonSecondary width="100%" onClick={() => handleBuyClick(error.token)} style={{ marginTop: '8px' }}>
              <Text fontSize="14px" textAlign="left">
                Buy {error.symbol}
              </Text>
            </ButtonSecondary>
          </Row>
        </Row>
      )
    }
    return null
  }

  return (
    <ReviewGrid>
      <StepLineModal
        isOpen={showStartModal}
        onDismiss={handleModalDismiss}
        steps={updateSteps}
        header={<ThemedText.DeprecatedMediumHeader>Update Farm configuration</ThemedText.DeprecatedMediumHeader>}
        onFinish={handleModalFinish}
      />

      {/* Changed Reward Tokens */}
      {rewardTokenHasChanged &&
        changedRewardTokens.map((rewardToken) => (
          <Container key={`reward-token-${rewardToken.index}`}>
            <CardHeader>
              <Text fontWeight={500}>Reward Token {rewardToken.index + 1}</Text>
            </CardHeader>
            <CardContent>
              {rewardToken.changes.map(({ field, current, previous }, index) => (
                <ReviewItem
                  key={`${rewardToken.index}-${field}-${index}`}
                  label={field}
                  currentValue={previous.toString()}
                  newValue={current.toString()}
                />
              ))}
            </CardContent>
          </Container>
        ))}

      {/* Changed General Fields */}
      {changedFields.length > 0 && (
        <Container>
          <CardHeader>
            <Text fontWeight={500}>General Settings</Text>
          </CardHeader>
          <CardContent>
            {changedFields.map(({ field, current, previous }) => (
              <ReviewItem
                key={field}
                label={field}
                currentValue={previous}
                newValue={current}
                info={getFieldInfo(field)}
              />
            ))}
          </CardContent>
        </Container>
      )}

      {error && renderError(error)}

      <FlexBetween style={{ gap: '1rem', marginTop: '2rem' }}>
        <ButtonSecondary onClick={onBack} style={{ width: 'fit-content', gap: '0.5rem' }}>
          <ArrowLeft size={16} />
          Back to Edit
        </ButtonSecondary>
        <ButtonPrimary
          variant="primary"
          onClick={handleConfirm}
          disabled={loading || !!error}
          style={{ width: 'fit-content' }}
        >
          {loading && <LoaderIcon size={16} />}
          Confirm Changes
        </ButtonPrimary>
      </FlexBetween>
    </ReviewGrid>
  )
}

export default ReviewForm
